import { memo, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  CheckCircle2,
  Circle,
  ScrollText,
  Swords,
  Award,
  Sparkles,
  Package,
  Shirt,
  Cat,
  Clock,
  MessageCircle,
  Handshake,
  Bookmark,
  AlertTriangle,
  Search,
  LayoutGrid,
  List,
  Images,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import type { PersonagemData } from "@/lib/public-anuncios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { syncFavoritesToServer } from "@/lib/favorites-sync";
import { isNotified } from "@/lib/notified-sold";
import { decodeOutfit, pickOutfitImage, outfitLabel, outfitAddonBadgeFromRaw, outfitAddonBadge } from "@/lib/outfit-selection";
import { AddonBadge } from "@/components/personagem/OutfitPicker";
import ShareMenu from "@/components/ShareMenu";
import ContactOptionsPopover from "@/components/ContactOptionsPopover";
import ReportProblemDialog from "@/components/ReportProblemDialog";
import SolicitarIntermedioDialog from "@/components/SolicitarIntermedioDialog";
import MediaCarouselDialog from "@/components/personagem/MediaCarouselDialog";
import OwnerActionButtons from "@/components/OwnerActionButtons";
import SoldRibbon from "@/components/SoldRibbon";
import { Loader2, Crown, QrCode, Copy, Store } from "lucide-react";
import { useAnuncioPaymentPolling } from "@/hooks/useAnuncioPaymentPolling";
import logo from "@/assets/logo.webp";
import rubiniCoinsIcon from "@/assets/rubini_coins.gif";
import crystalCoinIcon from "@/assets/crystal_coin.gif";
import outfitCitizen from "@/assets/outfit-citizen.png";
import { useIsMobile } from "@/hooks/use-mobile";

interface PersonagemCardProps {
  anuncioId: string;
  personagemNome: string;
  preco: number;
  precoCoins?: number | null;
  precoKk?: number | null;
  moeda?: string;
  servidor: string | null;
  destaque: boolean;
  apelido?: string | null;
  isRevendedor?: boolean;
  expiraEm?: string | null;
  descricao?: string | null;
  tier?: number | null;
  temWhatsapp?: boolean;
  temEmail?: boolean;
  anuncianteId?: string | null;
  anuncianteAvatarUrl?: string | null;
  preferenciasContato?: string[];
  sessionContato?: string;
  sessionToken?: string;
  hasActiveIntermediacao?: boolean;
  onIntermediacaoClick?: () => void;
  onRequireLogin?: (action: () => void) => void;
  onOpenProfile?: (id: string) => void;
  isOwner?: boolean;
  anuncioStatus?: string;
  onAnuncioAction?: () => void;
  personagem: PersonagemData;
  onClick?: () => void;
  createdAt?: string | null;
  destaqueInicio?: string | null;
  renovacaoPendente?: boolean;
  taxaPaga?: boolean;
  metodoPagamentoTaxa?: string | null;
  valorTaxa?: number | null;
}

const SKILLS: { key: keyof PersonagemData; label: string }[] = [
  { key: "magic", label: "Magic" },
  { key: "club", label: "Club" },
  { key: "fist", label: "Fist" },
  { key: "sword", label: "Sword" },
  { key: "fishing", label: "Fishing" },
  { key: "axe", label: "Axe" },
  { key: "distance", label: "Distance" },
  { key: "shielding", label: "Shield" },
];

const CHECKS: { key: keyof PersonagemData; label: string }[] = [
  { key: "weekly_task", label: "Weekly Task" },
  { key: "prey_slot", label: "Prey Slot" },
  { key: "charm_expansion", label: "Charm Exp." },
  { key: "loot_pouch", label: "Loot Pouch" },
];

const formatExpira = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d
    .toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", "");
};

const itemImageUrl = (name: string) =>
  `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(name.replace(/ /g, "_"))}`;

// Rubinot's outfit-proxy returns an animated sprite strip (e.g. 4096x128) when
// animated=1/walk=1, which renders as an invisible sliver inside small thumbnails.
// Force a single static frame so the outfit image is visible.
const staticOutfitUrl = (raw?: string | null) => {
  if (!raw) return raw;
  if (!/outfit-proxy/i.test(raw)) return raw;
  return raw
    .replace(/([?&])animated=1\b/i, "$1animated=0")
    .replace(/([?&])walk=1\b/i, "$1walk=0");
};

const proxyImageUrl = (raw?: string | null) =>
  raw
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?url=${encodeURIComponent(raw)}`
    : null;

const stripTier = (name: string) => name.replace(/\s*\(Tier.*\)\s*$/i, "").trim();

const extractTier = (name: string): string | null => {
  const m = name.match(/\(Tier\s*([^)]+)\)\s*$/i);
  return m ? m[1].trim() : null;
};

const getFavorites = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem("rpgcoin_favorites") || "[]");
  } catch {
    return [];
  }
};

const toggleFavorite = (id: string): boolean => {
  const favs = getFavorites();
  const idx = favs.indexOf(id);
  if (idx >= 0) {
    favs.splice(idx, 1);
    localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
    return false;
  }
  favs.push(id);
  localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
  return true;
};

const PersonagemCard = ({
  anuncioId,
  personagemNome,
  preco,
  precoCoins,
  precoKk,
  moeda = "BRL",
  servidor,
  destaque,
  apelido,
  isRevendedor,
  expiraEm,
  descricao,
  tier,
  temWhatsapp,
  temEmail,
  anuncianteId,
  anuncianteAvatarUrl,
  preferenciasContato = ["plataforma"],
  sessionContato,
  sessionToken,
  hasActiveIntermediacao,
  onIntermediacaoClick,
  onRequireLogin,
  onOpenProfile,
  isOwner,
  anuncioStatus,
  onAnuncioAction,
  personagem,
  onClick,
  createdAt,
  destaqueInicio,
  renovacaoPendente,
  taxaPaga,
  metodoPagamentoTaxa,
  valorTaxa,
}: PersonagemCardProps) => {
  const hasReais = preco > 0;
  const hasCoins = precoCoins != null && precoCoins > 0;
  const hasKk = precoKk != null && precoKk > 0;

  const isMobile = useIsMobile();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [paymentQr, setPaymentQr] = useState<{ base64: string; text: string | null; valorCobrado?: number } | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const isPaused = anuncioStatus === "pausado";
  const isPending = anuncioStatus === "pendente" || anuncioStatus === "aguardando_pagamento";

  // Owner payment / renewal state (mirrors MarketplaceCard item logic)
  const isDateExpired = !!(expiraEm && new Date(expiraEm) <= new Date()) && anuncioStatus !== "vendido";
  const isRenewalPending = !!renovacaoPendente && !isDateExpired
    && anuncioStatus !== "aguardando_pagamento" && anuncioStatus !== "encerrado"
    && anuncioStatus !== "vendido" && anuncioStatus !== "rejeitado"
    && anuncioStatus !== "expirado" && anuncioStatus !== "pagamento_expirado";
  const isRenewalPaidByCoins = isRenewalPending && metodoPagamentoTaxa === "coins" && taxaPaga === true;
  const isAwaitingPayment = anuncioStatus === "aguardando_pagamento" || isRenewalPending;

  const { confirmed: paymentConfirmed } = useAnuncioPaymentPolling({
    active: !!paymentQr && isAwaitingPayment,
    anuncioId,
    sessionContato,
    sessionToken,
    onConfirmed: () => {
      setTimeout(() => {
        setPaymentQr(null);
        onAnuncioAction?.();
      }, 1500);
    },
  });

  useEffect(() => { setDismissed(false); }, [anuncioStatus]);
  const handleOwnerAction = () => {
    setDismissed(true);
    setTimeout(() => onAnuncioAction?.(), 400);
  };

  const handlePayClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (loadingPayment) return;
    setLoadingPayment(true);
    try {
      const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
      const { data, error } = await supabase.functions.invoke("client-anuncio-action", {
        body: { contato: sess.contato, token: sess.token, anuncio_id: anuncioId, acao: "regerar_pagamento" },
      });
      if (!error && data?.pix_qr_code_base64) {
        setPaymentQr({ base64: data.pix_qr_code_base64, text: data.pix_qr_code || null, valorCobrado: data.valor_cobrado });
      } else {
        toast({ title: data?.error || "Erro ao gerar QR Code", variant: "destructive" });
      }
    } catch {
      toast({ title: "Erro ao gerar pagamento", variant: "destructive" });
    }
    setLoadingPayment(false);
  };

  const handleCancelRenovacao = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (actionLoading) return;
    setActionLoading("cancelar_renovacao");
    try {
      const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
      const { data, error } = await supabase.functions.invoke("client-anuncio-action", {
        body: { contato: sess.contato, token: sess.token, anuncio_id: anuncioId, acao: "cancelar_renovacao" },
      });
      if (error || data?.error) {
        toast({ title: data?.error || "Erro ao cancelar renovação", variant: "destructive" });
      } else {
        toast({ title: "Renovação cancelada" });
        setPaymentQr(null);
        onAnuncioAction?.();
      }
    } catch {
      toast({ title: "Erro ao cancelar renovação", variant: "destructive" });
    }
    setActionLoading(null);
  };
  const [dialogKey, setDialogKey] = useState<string | null>(null);
  const [combinedOpen, setCombinedOpen] = useState(false);
  const [dialogSearch, setDialogSearch] = useState("");
  const [viewMode, setViewMode] = useState<"lista" | "grade">("lista");
  const [collapsedCats, setCollapsedCats] = useState<Record<string, boolean>>({});
  const [galleryCats, setGalleryCats] = useState<Record<string, boolean>>({});
  const [outfitImgs, setOutfitImgs] = useState<Record<string, string | null>>({});
  const [montariaImgs, setMontariaImgs] = useState<Record<string, string | null>>({});
  const [charmImgs, setCharmImgs] = useState<Record<string, string | null>>({});
  const [questImgs, setQuestImgs] = useState<Record<string, string | null>>({});
  const [charmInfo, setCharmInfo] = useState<Record<string, { tipo: string | null; descricao: string | null; custo: string | null }>>({});
  const [montariaInfo, setMontariaInfo] = useState<Record<string, Record<string, string | null>>>({});
  const [questInfo, setQuestInfo] = useState<Record<string, Record<string, string | null>>>({});
  const [outfitInfo, setOutfitInfo] = useState<Record<string, Record<string, string | null>>>({});
  const [itemDetail, setItemDetail] = useState<{
    kind: "item" | "outfit" | "montaria" | "charm" | "quest";
    name: string;
    img: string | null;
    tier: string | null;
    info: { label: string; value: string }[];
    loading: boolean;
  } | null>(null);
  const [isFav, setIsFav] = useState(() => getFavorites().includes(anuncioId));
  const [reportOpen, setReportOpen] = useState(false);
  const [intermedioOpen, setIntermedioOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [isReported, setIsReported] = useState(() => isNotified(anuncioId));

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    const doFav = () => {
      const added = toggleFavorite(anuncioId);
      setIsFav(added);
      syncFavoritesToServer();
      toast({ title: added ? "Adicionado à lista!" : "Removido da lista" });
    };
    if (onRequireLogin) onRequireLogin(doFav);
    else doFav();
  };

  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const doOpen = () => setReportOpen(true);
    if (onRequireLogin) onRequireLogin(doOpen);
    else doOpen();
  };

  const charmPoints = Number(personagem.charm_points ?? 0);

  const selections: { key: string; icon: typeof ScrollText; label: string; tabLabel?: string; items: string[]; displayValue?: number; imageKind?: "item" | "outfit" | "montaria" | "charm" | "quest" }[] = [
    { key: "quests", icon: ScrollText, label: "Quests", items: personagem.quests || [], imageKind: "quest" },
    { key: "charms", icon: Sparkles, label: "Charm Points", tabLabel: "Charms", items: personagem.charms || [], displayValue: charmPoints, imageKind: "charm" },
    { key: "itens", icon: Package, label: "Itens", items: personagem.itens || [], imageKind: "item" },
    { key: "outfits", icon: Shirt, label: "Outfits", items: personagem.outfits || [], imageKind: "outfit" },
    { key: "montarias", icon: Cat, label: "Montarias", items: personagem.montarias || [], imageKind: "montaria" },
  ];

  // Lazily load outfit / montaria / charm images when the dialog opens
  useEffect(() => {
    if (dialogKey === null && !combinedOpen) return;
    const outfitItems = personagem.outfits || [];
    const montariaItems = personagem.montarias || [];
    const charmItems = personagem.charms || [];
    if (outfitItems.length > 0 && Object.keys(outfitImgs).length === 0) {
      const baseNames = [...new Set(outfitItems.map((raw) => decodeOutfit(raw).nome))];
      (supabase as any)
        .from("outfits")
        .select("nome, genero, tipo, update_versao, img_outfit, img_addon1, img_addon2, img_completo")
        .in("nome", baseNames)
        .then(({ data }: { data: any[] | null }) => {
          const recByKey = new Map<string, any>();
          (data || []).forEach((o) => {
            recByKey.set(`${o.nome}__${o.genero || ""}`, {
              img_outfit: proxyImageUrl(staticOutfitUrl(o.img_outfit)),
              img_addon1: proxyImageUrl(staticOutfitUrl(o.img_addon1)),
              img_addon2: proxyImageUrl(staticOutfitUrl(o.img_addon2)),
              img_completo: proxyImageUrl(staticOutfitUrl(o.img_completo)),
              tipo: o.tipo ?? null,
              genero: o.genero ?? null,
              update_versao: o.update_versao ?? null,
            });
          });
          const m: Record<string, string | null> = {};
          const info: Record<string, Record<string, string | null>> = {};
          outfitItems.forEach((raw) => {
            const { nome, genero, addons } = decodeOutfit(raw);
            // Match exact gender, else fall back to any record for that name.
            const rec =
              recByKey.get(`${nome}__${genero}`) ||
              [...recByKey.entries()].find(([key]) => key.startsWith(`${nome}__`))?.[1] ||
              null;
            m[raw] = pickOutfitImage(rec, addons);
            info[raw] = {
              Tipo: rec?.tipo ?? null,
              Gênero: rec?.genero ?? genero ?? null,
              Versão: rec?.update_versao ?? null,
            };
          });
          setOutfitImgs(m);
          setOutfitInfo(info);
        });
    }
    if (montariaItems.length > 0 && Object.keys(montariaImgs).length === 0) {
      (supabase as any)
        .from("montarias")
        .select("nome, img_montaria, velocidade, metodo_domar, descricao, premium, update_versao, release_data")
        .in("nome", montariaItems)
        .then(({ data }: { data: any[] | null }) => {
          const m: Record<string, string | null> = {};
          const info: Record<string, Record<string, string | null>> = {};
          (data || []).forEach((o) => {
            m[o.nome] = proxyImageUrl(o.img_montaria);
            info[o.nome] = {
              "Método para Domar": o.metodo_domar ?? null,
              Velocidade: o.velocidade ?? null,
              Premium: o.premium != null ? (o.premium ? "Sim" : "Não") : null,
              Versão: o.update_versao ?? null,
              Lançamento: o.release_data ?? null,
              Descrição: o.descricao ?? null,
            };
          });
          setMontariaImgs(m);
          setMontariaInfo(info);
        });
    }
    if (charmItems.length > 0 && Object.keys(charmImgs).length === 0) {
      (supabase as any)
        .from("charms")
        .select("nome, img, tipo, descricao, custo")
        .in("nome", charmItems)
        .then(({ data }: { data: any[] | null }) => {
          const m: Record<string, string | null> = {};
          const info: Record<string, { tipo: string | null; descricao: string | null; custo: string | null }> = {};
          (data || []).forEach((o) => {
            m[o.nome] = proxyImageUrl(o.img);
            info[o.nome] = { tipo: o.tipo ?? null, descricao: o.descricao ?? null, custo: o.custo ?? null };
          });
          setCharmImgs(m);
          setCharmInfo(info);
        });
    }
    const questItems = personagem.quests || [];
    if (questItems.length > 0 && Object.keys(questImgs).length === 0) {
      (supabase as any)
        .from("quests")
        .select("nome, img_premium, level, localizacao, recompensa, premium")
        .in("nome", questItems)
        .then(({ data }: { data: any[] | null }) => {
          const m: Record<string, string | null> = {};
          const info: Record<string, Record<string, string | null>> = {};
          (data || []).forEach((o) => {
            m[o.nome] = proxyImageUrl(o.img_premium);
            info[o.nome] = {
              Level: o.level ?? null,
              Localização: o.localizacao ?? null,
              Recompensa: o.recompensa ?? null,
              Premium: o.premium != null ? (o.premium ? "Sim" : "Não") : null,
            };
          });
          setQuestImgs(m);
          setQuestInfo(info);
        });
    }
  }, [dialogKey, combinedOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const getItemImg = (kind: "item" | "outfit" | "montaria" | "charm" | "quest" | undefined, name: string): string | null => {
    if (!kind) return null;
    const clean = stripTier(name);
    if (kind === "item") return itemImageUrl(clean);
    if (kind === "outfit") return outfitImgs[name] ?? null;
    if (kind === "montaria") return montariaImgs[clean] ?? null;
    if (kind === "charm") return charmImgs[clean] ?? null;
    if (kind === "quest") return questImgs[clean] ?? null;
    return null;
  };

  const openDetail = (
    kind: "item" | "outfit" | "montaria" | "charm" | "quest" | undefined,
    rawName: string,
  ) => {
    if (!kind) return;
    const clean = stripTier(rawName);
    const tier = kind === "item" ? extractTier(rawName) : null;
    const img = getItemImg(kind, rawName);

    const buildInfo = (src?: Record<string, string | null> | null) =>
      Object.entries(src ?? {})
        .filter(([, v]) => v != null && String(v).trim() !== "")
        .map(([label, value]) => ({ label, value: String(value) }));

    if (kind === "montaria") {
      setItemDetail({ kind, name: clean, img, tier, info: buildInfo(montariaInfo[clean]), loading: false });
      return;
    }
    if (kind === "outfit") {
      setItemDetail({ kind, name: outfitLabel(rawName), img, tier, info: buildInfo(outfitInfo[rawName]), loading: false });
      return;
    }
    if (kind === "quest") {
      setItemDetail({ kind, name: clean, img, tier, info: buildInfo(questInfo[clean]), loading: false });
      return;
    }
    if (kind === "charm") {
      const c = charmInfo[clean];
      setItemDetail({
        kind,
        name: clean,
        img,
        tier,
        info: buildInfo({ Tipo: c?.tipo ?? null, Custo: c?.custo ?? null, Descrição: c?.descricao ?? null }),
        loading: false,
      });
      return;
    }
    // item: fetch extra details from tibia_items on demand
    setItemDetail({ kind, name: clean, img, tier, info: [], loading: true });
    (supabase as any)
      .from("tibia_items")
      .select("levelrequired, vocrequired, attack, defense, weight, slot, weapontype, primarytype, value")
      .eq("name", clean)
      .maybeSingle()
      .then(({ data }: { data: any | null }) => {
        const info = buildInfo({
          Level: data?.levelrequired != null ? String(data.levelrequired) : null,
          Vocação: data?.vocrequired ?? null,
          Ataque: data?.attack != null ? String(data.attack) : null,
          Defesa: data?.defense != null ? String(data.defense) : null,
          Peso: data?.weight != null ? String(data.weight) : null,
          Slot: data?.slot ?? null,
          Tipo: data?.weapontype ?? data?.primarytype ?? null,
          Valor: data?.value != null ? String(data.value) : null,
        });
        setItemDetail((prev) => (prev && prev.name === clean ? { ...prev, info, loading: false } : prev));
      });
  };

  const bossPoints = Number(personagem.boss_points ?? 0);
  const loyaltyPoints = Number(personagem.loyalty_points ?? 0);

  const sexoRaw = (personagem.sexo || "").trim().toLowerCase();
  const genderSymbol =
    sexoRaw.startsWith("m")
      ? { symbol: "♂", color: "#4aa3ff" }
      : sexoRaw.startsWith("f")
        ? { symbol: "♀", color: "#ff6fb5" }
        : null;

  const battlePass = (personagem.battle_pass || []).filter((n) => n != null);
  const midias = (personagem.midias || []).filter((m) => m && m.path);
  const auras = (personagem.auras || []).filter(Boolean);

  const expira = formatExpira(expiraEm);


  return (
    <>
      <div
        onClick={() => { onClick?.(); setCombinedOpen(true); }}
        className={`tibia-border rounded-sm bg-card overflow-hidden cursor-pointer transition-all hover:brightness-110 flex flex-col relative ${
          destaque ? "ring-1 ring-primary/60 gold-glow" : ""
        } ${dismissed ? "opacity-0 scale-90 translate-y-5" : "opacity-100"}`}
      >
        {/* Owner badge */}
        {isOwner && (
          <div className="absolute top-1 right-1 z-20 flex items-center gap-0.5 bg-primary/90 text-primary-foreground px-1.5 py-0.5 rounded-sm">
            <Crown className="w-3 h-3" />
            <span className="font-pixel text-[6px] tracking-wider">MEU</span>
          </div>
        )}
        {/* Loading overlay during pause/resume action */}
        {(actionLoading === "pausar" || actionLoading === "retomar") && (
          <div className="absolute inset-0 bg-black/40 z-30 rounded-sm flex items-center justify-center pointer-events-none">
            <div className="bg-card/90 px-3 py-1.5 rounded-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
              <span className="font-pixel text-[8px] text-amber-300 tracking-wider font-bold">
                {actionLoading === "pausar" ? "PAUSANDO..." : "RETOMANDO..."}
              </span>
            </div>
          </div>
        )}
        {/* Paused overlay */}
        {isPaused && !isDateExpired && !isRenewalPending && actionLoading !== "retomar" && (
          <div className="absolute inset-0 bg-black/50 z-20 rounded-sm flex items-center justify-center pointer-events-none">
            <div className="bg-yellow-500/90 px-3 py-1 rounded-sm">
              <span className="font-pixel text-[8px] text-black tracking-wider font-bold">ANÚNCIO PAUSADO</span>
            </div>
          </div>
        )}
        {/* Expired by date overlay */}
        {isDateExpired && !isRenewalPending && anuncioStatus !== "pendente" && anuncioStatus !== "aguardando_pagamento" && anuncioStatus !== "vendido" && (
          <div className="absolute inset-0 bg-black/60 z-20 rounded-sm flex items-center justify-center pointer-events-none">
            <div className="bg-muted/90 px-3 py-1 rounded-sm">
              <span className="font-pixel text-[8px] text-muted-foreground tracking-wider font-bold">ENCERRADO</span>
            </div>
          </div>
        )}
        {/* Pending approval overlay */}
        {anuncioStatus === "pendente" && (
          <div className="absolute inset-0 bg-amber-900/40 z-20 rounded-sm flex items-center justify-center pointer-events-none">
            <div className="bg-amber-500/90 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-black" />
              <span className="font-pixel text-[7px] text-black tracking-wider font-bold">AGUARDANDO APROVAÇÃO</span>
            </div>
          </div>
        )}
        {/* Awaiting payment overlay (with Pagar button) */}
        {anuncioStatus === "aguardando_pagamento" && isOwner && (
          <div className="absolute inset-0 bg-amber-900/40 z-30 rounded-sm flex flex-col items-center justify-center gap-2 pointer-events-none">
            <div className="bg-amber-500/90 px-3 py-1.5 rounded-sm flex flex-col items-center gap-0.5">
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-black" />
                <span className="font-pixel text-[7px] text-black tracking-wider font-bold">AGUARDANDO PAGAMENTO</span>
              </div>
              {createdAt && (
                <span className="font-pixel text-[7px] text-black/70 tracking-wider">
                  até {(() => { try { return new Date(new Date(createdAt).getTime() + 24 * 60 * 60 * 1000).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } })()}
                </span>
              )}
            </div>
            <button
              onClick={handlePayClick}
              disabled={loadingPayment}
              className="pointer-events-auto flex items-center gap-1 px-3 py-1 rounded-sm text-[8px] font-pixel bg-primary/90 text-primary-foreground border border-primary/60 hover:bg-primary transition-colors shadow-md disabled:opacity-50"
            >
              {loadingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
              {loadingPayment ? "Gerando..." : "Pagar"}
            </button>
          </div>
        )}
        {/* Awaiting payment (non-owner placeholder) */}
        {anuncioStatus === "aguardando_pagamento" && !isOwner && (
          <div className="absolute inset-0 bg-black/50 z-20 rounded-sm flex items-center justify-center pointer-events-none">
            <div className="bg-primary/90 px-3 py-1 rounded-sm">
              <span className="font-pixel text-[8px] text-primary-foreground tracking-wider font-bold">AGUARDANDO PAGAMENTO</span>
            </div>
          </div>
        )}
        {/* Renewal pending overlay (blue) - only visible to owner */}
        {isRenewalPending && isOwner && (
          <div className="absolute inset-0 bg-blue-900/40 z-30 rounded-sm flex flex-col items-center justify-center gap-2 pointer-events-none">
            <div className="bg-blue-500/90 px-3 py-1.5 rounded-sm flex flex-col items-center gap-0.5">
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-white" />
                <span className="font-pixel text-[7px] text-white tracking-wider font-bold">RENOVAÇÃO PENDENTE</span>
              </div>
              <span className="font-pixel text-[6px] text-white/80 tracking-wider">
                {isRenewalPaidByCoins ? "Aguardando aprovação" : "Aguardando pagamento"}
              </span>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              {!isRenewalPaidByCoins && (
                <button
                  onClick={handlePayClick}
                  disabled={loadingPayment || actionLoading === "cancelar_renovacao"}
                  className="pointer-events-auto flex items-center gap-1 px-3 py-1 rounded-sm text-[8px] font-pixel bg-primary/90 text-primary-foreground border border-primary/60 hover:bg-primary transition-colors shadow-md disabled:opacity-50"
                >
                  {loadingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                  {loadingPayment ? "Gerando..." : "Pagar Renovação"}
                </button>
              )}
              <button
                onClick={handleCancelRenovacao}
                disabled={!!actionLoading}
                className="pointer-events-auto flex items-center gap-1 px-3 py-1 rounded-sm text-[8px] font-pixel bg-destructive/90 text-destructive-foreground border border-destructive/60 hover:bg-destructive transition-colors shadow-md disabled:opacity-50"
              >
                {actionLoading === "cancelar_renovacao" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Cancelar
              </button>
            </div>
          </div>
        )}
        {anuncioStatus === "vendido" && <SoldRibbon />}
        {anuncioStatus === "encerrado" && <SoldRibbon type="cancelado" />}
        {/* Inline QR Code payment overlay */}
        {paymentQr && (isAwaitingPayment || paymentConfirmed) && (
          <div
            className="absolute inset-0 bg-card/95 z-40 rounded-sm flex flex-col items-center justify-start gap-1 p-1 pt-2 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {paymentConfirmed ? (
              <div className="flex flex-col items-center gap-2 py-4 animate-fade-in">
                <div className="w-14 h-14 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-pixel text-[10px] text-green-500 font-bold">Pagamento Confirmado!</p>
                <p className="font-pixel text-[7px] text-muted-foreground text-center">Seu anúncio está sendo processado.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 mb-1">
                  <img src={outfitCitizen} alt={personagemNome} width={24} height={24} className="w-6 h-6 object-contain" loading="lazy" />
                  <span className="font-pixel text-[8px] text-foreground leading-none truncate max-w-[140px]">{personagemNome}</span>
                </div>
                <span className="font-pixel text-[10px] text-green-400 font-bold drop-shadow-[0_0_4px_rgba(74,222,128,0.5)] leading-none">
                  R$ {Number(paymentQr.valorCobrado ?? valorTaxa ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </span>
                <span className="font-pixel text-[7px] text-muted-foreground leading-none">Taxa do anúncio</span>
                <div className="relative w-36 h-36">
                  <img src={`data:image/png;base64,${paymentQr.base64}`} alt="QR Code PIX" className="w-36 h-36 rounded-sm border border-border" />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md">
                      <img src={logo} alt="Logo" className="w-6 h-6 rounded-full object-cover" />
                    </div>
                  </div>
                </div>
                {paymentQr.text && (
                  <button
                    onClick={() => { navigator.clipboard.writeText(paymentQr.text!); toast({ title: "Código PIX copiado!" }); }}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[7px] font-pixel bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    Copiar código
                  </button>
                )}
                <div className="flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                  <span className="text-[7px] text-muted-foreground">Aguardando confirmação...</span>
                </div>
                <button onClick={() => setPaymentQr(null)} className="text-[7px] font-pixel text-muted-foreground hover:text-foreground transition-colors">Fechar</button>
              </>
            )}
          </div>
        )}
        {/* Header with image, name, level, vocation */}
        <div className="px-2.5 py-2 border-b border-border/50 bg-[hsl(20_8%_8%)] flex items-stretch gap-2.5">
          <img
            src={outfitCitizen}
            alt={personagemNome}
            width={512}
            height={512}
            loading="lazy"
            className="w-12 h-12 object-contain shrink-0 drop-shadow-[0_0_6px_hsl(var(--primary)/0.4)]"
          />
          <div className="min-w-0 flex-1">
            <h3 className="font-pixel text-[10px] text-primary gold-text-glow truncate flex items-center gap-1">
              <span className="truncate">{personagemNome}</span>
              {genderSymbol && (
                <span className="shrink-0" style={{ color: genderSymbol.color }}>{genderSymbol.symbol}</span>
              )}
            </h3>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {personagem.level != null && (
                <span className="font-pixel text-[7px] text-foreground">Lvl {personagem.level}</span>
              )}
              {personagem.vocacao && (
                <span className="font-pixel text-[7px] text-muted-foreground truncate">
                  {personagem.vocacao}
                </span>
              )}
            </div>
            {(servidor || personagem.servidor) && (
              <div className="mt-0.5">
                <span className="font-pixel text-[7px] text-amber-400/90 truncate">
                  {servidor || personagem.servidor}
                </span>
              </div>
            )}
          </div>
          {/* Right column: advertiser badge (top) + expiration (bottom) */}
          <div className="flex flex-col items-end justify-between shrink-0 gap-1">
            {!isOwner && apelido ? (
              <button
                type="button"
                title={isRevendedor ? "Revendedor" : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (anuncianteId) onOpenProfile?.(anuncianteId);
                }}
                disabled={!anuncianteId}
                className={`inline-flex items-center gap-1 font-pixel text-[6.5px] tibia-border-inner rounded-sm px-1.5 py-0.5 truncate max-w-[90px] transition-colors disabled:cursor-default ${
                  isRevendedor
                    ? "bg-indigo-600/90 border-indigo-400 text-white hover:bg-indigo-500 shadow-[0_0_8px_hsl(231_85%_60%/0.6)]"
                    : "text-primary bg-stone-dark/60 hover:bg-stone-dark/90 hover:text-primary"
                }`}
              >
                {isRevendedor && <Store className="w-2 h-2 shrink-0" />}
                {apelido}
              </button>
            ) : (
              <span />
            )}
            {expira && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Clock className="w-2.5 h-2.5 shrink-0" />
                <span className="font-pixel text-[6.5px] whitespace-nowrap">{expira}</span>
              </span>
            )}
          </div>
        </div>


        {/* Skills with progress bars */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 px-2.5 py-2">
          {SKILLS.map((s) => {
            const pct = Number(personagem[`${String(s.key)}_pct` as keyof PersonagemData] ?? 0);
            return (
              <div key={s.key} className="flex flex-col gap-0.5">
                <div className="flex items-center justify-between">
                  <span className="font-pixel text-[6.5px] text-muted-foreground">{s.label}</span>
                  <span className="font-pixel text-[7px] text-foreground tabular-nums">
                    {Number(personagem[s.key] ?? 0)}
                  </span>

                </div>
                <div className="h-1 w-full rounded-full bg-stone-dark/80 overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Checks */}
        <div className="px-2.5 pb-2 flex flex-row flex-wrap items-center gap-x-3 gap-y-1">
          {CHECKS.map((c) => {
            const active = Boolean(personagem[c.key]);
            return (
              <div key={c.key} className="flex items-center gap-1">
                {active ? (
                  <CheckCircle2 className="w-2.5 h-2.5 text-green-400 shrink-0" />
                ) : (
                  <Circle className="w-2.5 h-2.5 text-muted-foreground/40 shrink-0" />
                )}
                <span
                  className={`font-pixel text-[6.5px] ${active ? "text-foreground" : "text-muted-foreground/60"}`}
                >
                  {c.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Auras */}
        {auras.length > 0 && (
          <div className="px-2.5 pb-2">
            <div className="flex items-center gap-1 mb-1">
              <Sparkles className="w-2.5 h-2.5 text-primary shrink-0" />
              <span className="font-pixel text-[6.5px] text-muted-foreground">Auras</span>
              {auras.length > 1 && (
                <span className="font-pixel text-[6px] text-primary tibia-border-inner rounded-sm px-1 py-0.5 bg-primary/10 gold-text-glow">
                  {auras.length}x
                </span>
              )}
            </div>
            <div className="flex flex-row flex-wrap items-center gap-1.5">
              {auras.map((a) => (
                <div
                  key={a}
                  onClick={(e) => e.stopPropagation()}
                  className={`flex items-center gap-1 tibia-border-inner rounded-sm px-1.5 py-1 cursor-default ${
                    auras.length > 1 ? "bg-primary/10 gold-glow" : "bg-stone-dark/40"
                  }`}
                  title={a}
                >
                  <img src={itemImageUrl(a)} alt={a} loading="lazy" className="w-4 h-4 object-contain shrink-0" />
                  <span className="font-pixel text-[6px] text-foreground truncate max-w-[90px]">{a}</span>
                </div>
              ))}
            </div>
          </div>
        )}



        {/* Selections */}
        <div className="px-2.5 pb-2 grid grid-cols-2 gap-1">
          {/* Boss Points (static value) */}
          <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 tibia-border-inner rounded-sm px-1.5 py-1 bg-stone-dark/40 text-left cursor-default">
            <Swords className="w-2.5 h-2.5 text-primary shrink-0" />
            <span className="font-pixel text-[6.5px] text-muted-foreground truncate">Boss Points</span>
            <span className="font-pixel text-[7px] text-foreground ml-auto">{bossPoints}</span>
          </div>
          <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 tibia-border-inner rounded-sm px-1.5 py-1 bg-stone-dark/40 text-left cursor-default">
            <Award className="w-2.5 h-2.5 text-primary shrink-0" />
            <span className="font-pixel text-[6.5px] text-muted-foreground truncate">Loyalty</span>
            <span className="font-pixel text-[7px] text-foreground ml-auto">{loyaltyPoints}</span>
          </div>
          {selections.map((sel) => (
            <button
              key={sel.label}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (sel.items.length > 0) setDialogKey(sel.key);
              }}
              className="flex items-center gap-1 tibia-border-inner rounded-sm px-1.5 py-1 bg-stone-dark/40 transition-colors hover:bg-stone-dark/70 text-left"
            >
              <sel.icon className="w-2.5 h-2.5 text-primary shrink-0" />
              <span className="font-pixel text-[6.5px] text-muted-foreground truncate">
                {sel.label}
              </span>
              <span className="font-pixel text-[7px] text-foreground ml-auto">{sel.displayValue ?? sel.items.length}</span>
            </button>
          ))}
        </div>

        {/* Battle pass + Media */}
        {(battlePass.length > 0 || midias.length > 0) && (
          <div className="px-2.5 pb-2 flex flex-row flex-wrap items-center gap-1">
            {battlePass.length > 0 && (
              <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 tibia-border-inner rounded-sm px-1.5 py-1 bg-stone-dark/40 cursor-default">
                <Sparkles className="w-2.5 h-2.5 text-primary shrink-0" />
                <span className="font-pixel text-[6.5px] text-muted-foreground">Passe de Batalha</span>
                <span className="font-pixel text-[7px] text-foreground">{battlePass.join(", ")}</span>
              </div>
            )}
            {midias.length > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setMediaOpen(true); }}
                className="flex items-center gap-1 tibia-border-inner rounded-sm px-1.5 py-1 bg-stone-dark/40 transition-colors hover:bg-stone-dark/70"
              >
                <Images className="w-2.5 h-2.5 text-primary shrink-0" />
                <span className="font-pixel text-[6.5px] text-muted-foreground">Fotos/Vídeos</span>
                <span className="font-pixel text-[7px] text-foreground">{midias.length}</span>
              </button>
            )}
          </div>
        )}

        {/* Description (wraps fully) */}
        <div className="mt-auto px-2.5 pb-2">
          <p className="font-pixel text-[6.5px] leading-[1.4] text-muted-foreground break-words whitespace-pre-wrap min-h-[18px]">
            {descricao?.trim() || ""}
          </p>
        </div>

        {/* Price */}
        <div className="px-2.5 py-2 border-t border-border/50 bg-stone-dark/60 flex flex-row flex-wrap items-center gap-x-3 gap-y-1">
          {hasReais && (
            <div className="flex items-center gap-1">
              <span className="font-pixel text-[9px] text-green-400 font-bold drop-shadow-[0_0_4px_rgba(74,222,128,0.5)]">
                R$
              </span>
              <span className="font-pixel text-[9px] text-primary font-bold">
                {preco.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
          {hasCoins && (
            <div className="flex items-center gap-1">
              <img src={rubiniCoinsIcon} alt="RC" className="w-3 h-3" />
              <span className="font-pixel text-[9px] text-foreground">
                {precoCoins!.toLocaleString("pt-BR")}
              </span>
            </div>
          )}
          {hasKk && (
            <div className="flex items-center gap-1">
              <img src={crystalCoinIcon} alt="KK" className="w-3 h-3" />
              <span className="font-pixel text-[9px] text-foreground">
                {precoKk!.toLocaleString("pt-BR")}
              </span>
            </div>
          )}
          {!hasReais && !hasCoins && !hasKk && (
            <span className="font-pixel text-[8px] text-amber-400">Proposta</span>
          )}
        </div>


        {/* Action buttons */}
        {isOwner ? (
          <div
            onClick={(e) => e.stopPropagation()}
            className={`${isPaused || isPending ? "relative z-40" : ""} bg-amber-300/20 border-t border-amber-400/30`}
          >
            <OwnerActionButtons
              anuncioId={anuncioId}
              anuncioStatus={anuncioStatus}
              expiraEm={expiraEm}
              isDestaque={destaque}
              destaqueInicio={destaqueInicio || null}
              sessionContato={sessionContato}
              sessionToken={sessionToken}
              actionLoading={actionLoading}
              setActionLoading={setActionLoading}
              onAnuncioAction={handleOwnerAction}
              onAnuncioRefresh={onAnuncioAction}
              renovacaoPendente={renovacaoPendente}
              itemNome={personagemNome}
              itemImageUrl={outfitCitizen}
            />
          </div>
        ) : (
        <div className="relative z-40 border-t border-border/40 flex items-center justify-between w-full py-1.5">

          <div className="flex-1 flex justify-center">
            <ContactOptionsPopover
              anuncioId={anuncioId}
              itemNome={personagemNome}
              temWhatsapp={temWhatsapp}
              temEmail={temEmail}
              anuncianteId={anuncianteId}
              anuncianteApelido={apelido}
              anuncianteAvatarUrl={anuncianteAvatarUrl}
              preferenciasContato={preferenciasContato}
              onRequireLogin={onRequireLogin}
              sessionContato={sessionContato}
              sessionToken={sessionToken}
            >
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button className="p-1 hover:scale-110 transition-all" onClick={(e) => e.stopPropagation()}>
                    <MessageCircle className="w-3.5 h-3.5 text-green-500/70 hover:text-green-400" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-[100]">Contatar</TooltipContent>
              </Tooltip>
            </ContactOptionsPopover>
          </div>

          <div className="flex-1 flex justify-center">
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const doOpen = () => setIntermedioOpen(true);
                    if (onRequireLogin) onRequireLogin(doOpen);
                    else doOpen();
                  }}
                  disabled={hasActiveIntermediacao}
                  className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <Handshake className="w-3.5 h-3.5 text-purple-400/70 hover:text-purple-400" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="z-[100]">
                {hasActiveIntermediacao ? "Em intermédio" : "Solicitar intermédio"}
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="flex-1 flex justify-center">
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button onClick={handleFavorite} className="p-1 hover:scale-110 transition-all">
                  <Bookmark className={`w-3.5 h-3.5 ${isFav ? "fill-primary text-primary" : "text-amber-400/70 hover:text-amber-400"}`} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="z-[100]">Adicionar à lista</TooltipContent>
            </Tooltip>
          </div>

          <div className="flex-1 flex justify-center">
            <ShareMenu
              url={`${window.location.origin}/item/${anuncioId}`}
              text={`Confira "${personagemNome}" no RPG Coin!`}
            />
          </div>

          <div className="flex-1 flex justify-center">
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button onClick={handleReportClick} disabled={isReported} className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400/70 hover:text-red-400" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="z-[100]">{isReported ? "Já reportado" : "Reportar problema"}</TooltipContent>
            </Tooltip>
           </div>
        </div>
        )}
      </div>


      <Dialog open={dialogKey !== null} onOpenChange={(o) => { if (!o) { setDialogKey(null); setDialogSearch(""); } }}>
        <DialogContent className={`${isMobile ? "max-w-sm" : "max-w-2xl"} max-h-[80vh] overflow-y-auto`}>
          {(() => {
            const activeSel = selections.find((s) => s.key === dialogKey);
            const tabbedSels = selections.filter((s) => s.items.length > 0);
            if (!activeSel) return null;
            const filtered = activeSel.items.filter((it) =>
              it.toLowerCase().includes(dialogSearch.trim().toLowerCase()),
            );
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-pixel text-[11px] text-primary gold-text-glow">
                    {(activeSel.tabLabel ?? activeSel.label)} ({activeSel.items.length})
                  </DialogTitle>
                </DialogHeader>

                {!isMobile && tabbedSels.length > 1 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {tabbedSels.map((s) => {
                      const isActive = s.key === dialogKey;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => { setDialogKey(s.key); setDialogSearch(""); }}
                          className={`flex items-center gap-1.5 tibia-border-inner rounded-sm px-2.5 py-1.5 font-pixel text-[7.5px] transition-colors ${isActive ? "bg-primary/20 text-primary" : "bg-stone-dark/40 text-muted-foreground hover:bg-stone-dark/70"}`}
                        >
                          <s.icon className="w-3 h-3 shrink-0" />
                          {(s.tabLabel ?? s.label)} ({s.items.length})
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-2">
                  <div className="tibia-input rounded-sm flex items-center gap-2 flex-1 min-w-0">
                    <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <input
                      value={dialogSearch}
                      onChange={(e) => setDialogSearch(e.target.value)}
                      placeholder="Pesquisar..."
                      className="bg-transparent text-xs w-full outline-none min-w-0"
                    />
                  </div>
                  <div className="flex items-center gap-1 tibia-border-inner rounded-sm p-0.5 bg-stone-dark/40 shrink-0">
                    <button
                      type="button"
                      onClick={() => setViewMode("lista")}
                      className={`p-1.5 rounded-sm transition-colors ${viewMode === "lista" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
                      aria-label="Ver em lista"
                    >
                      <List className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("grade")}
                      className={`p-1.5 rounded-sm transition-colors ${viewMode === "grade" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
                      aria-label="Ver em grade"
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {viewMode === "grade" ? (
                  activeSel.imageKind === "quest" ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {filtered.map((it, i) => {
                        const img = getItemImg(activeSel.imageKind, it);
                        return (
                          <button
                            type="button"
                            key={`${it}-${i}`}
                            onClick={() => openDetail(activeSel.imageKind, it)}
                            className="tibia-border-inner rounded-sm bg-stone-dark/40 px-2 py-1.5 flex items-center gap-1.5 text-left transition-all hover:brightness-110 hover:ring-1 hover:ring-primary/60 cursor-pointer"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                            <span className="w-6 h-6 shrink-0 flex items-center justify-center bg-stone-dark/40 rounded-sm overflow-hidden">
                              {img ? (
                                <img
                                  src={img}
                                  alt={it}
                                  loading="lazy"
                                  className="w-full h-full object-contain"
                                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                                />
                              ) : (
                                <span className="font-pixel text-[7px] text-muted-foreground/50">?</span>
                              )}
                            </span>
                            <span className="font-pixel text-[6.5px] leading-tight text-foreground break-words line-clamp-2 min-w-0">{it}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                    {filtered.map((it, i) => {
                      const img = getItemImg(activeSel.imageKind, it);
                      const tierVal = activeSel.imageKind === "item" ? extractTier(it) : null;
                      return (
                        <button
                          type="button"
                          key={`${it}-${i}`}
                          onClick={() => openDetail(activeSel.imageKind, it)}
                          className="tibia-border-inner rounded-sm bg-stone-dark/40 p-1.5 flex flex-col items-center gap-1 text-center transition-all hover:brightness-110 hover:ring-1 hover:ring-primary/60 cursor-pointer"
                        >
                          <div className="relative">
                            <span className="relative w-10 h-10 shrink-0 flex items-center justify-center bg-stone-dark/40 rounded-sm overflow-hidden">
                              {img ? (
                                <img
                                  src={img}
                                  alt={it}
                                  loading="lazy"
                                  className={`w-full h-full object-contain ${activeSel.imageKind === "outfit" ? "scale-110 object-right-bottom origin-bottom-right" : ""}`}
                                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                                />
                              ) : (
                                <span className="font-pixel text-[6px] text-muted-foreground/50">?</span>
                              )}
                              {tierVal && (
                                <span className="absolute top-0 right-0 w-4 h-4 rounded-full bg-[#FF8C00] flex items-center justify-center border-2 border-[#FF6B00] shadow-md">
                                  <span className="font-pixel text-[6px] font-bold text-white leading-none">{tierVal}</span>
                                </span>
                              )}
                            </span>
                            {activeSel.imageKind === "outfit" && <AddonBadge addons={decodeOutfit(it).addons} />}
                          </div>
                          <span className="font-pixel text-[6.5px] leading-tight text-foreground break-words line-clamp-2">{activeSel.imageKind === "item" ? stripTier(it) : activeSel.imageKind === "outfit" ? decodeOutfit(it).nome : it}</span>
                        </button>
                      );
                    })}
                  </div>
                  )
                ) : (
                  <ul className="flex flex-col gap-1.5 mt-2">
                    {filtered.map((it, i) => {
                      const img = getItemImg(activeSel.imageKind, it);
                      const cInfo = activeSel.imageKind === "charm" ? charmInfo[stripTier(it)] : null;
                      const tierVal = activeSel.imageKind === "item" ? extractTier(it) : null;
                      return (
                        <li key={`${it}-${i}`}>
                          <button
                            type="button"
                            onClick={() => openDetail(activeSel.imageKind, it)}
                            className="w-full flex items-start gap-2 font-pixel text-[8px] text-foreground tibia-border-inner rounded-sm px-2 py-1.5 bg-stone-dark/40 text-left transition-all hover:brightness-110 hover:ring-1 hover:ring-primary/60 cursor-pointer"
                          >
                            {activeSel.imageKind && (
                              <span className="w-7 h-7 shrink-0 flex items-center justify-center bg-stone-dark/40 rounded-sm overflow-hidden">
                              {img && (
                                  <img
                                    src={img}
                                    alt={it}
                                    loading="lazy"
                                    className={`w-full h-full object-contain ${activeSel.imageKind === "outfit" ? "scale-125" : ""}`}
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                                  />
                                )}
                              </span>
                            )}
                            {tierVal && (
                              <span className="w-4 h-4 shrink-0 self-center rounded-full bg-[#FF8C00] flex items-center justify-center border-2 border-[#FF6B00] shadow-md">
                                <span className="font-pixel text-[6px] font-bold text-white leading-none">{tierVal}</span>
                              </span>
                            )}
                            <div className="min-w-0 flex flex-col gap-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="min-w-0 break-words">{activeSel.imageKind === "item" ? stripTier(it) : activeSel.imageKind === "outfit" ? decodeOutfit(it).nome : it}</span>
                                {activeSel.imageKind === "outfit" && outfitAddonBadgeFromRaw(it) && (
                                  <span className="px-1.5 py-0.5 rounded-sm bg-primary text-primary-foreground text-[6.5px] font-bold leading-none whitespace-nowrap">
                                    {outfitAddonBadgeFromRaw(it)}
                                  </span>
                                )}
                                {cInfo?.tipo && (
                                  <span className="font-pixel text-[6.5px] text-primary tibia-border-inner rounded-sm px-1 py-0.5 bg-stone-dark/60 whitespace-nowrap">
                                    {cInfo.tipo}
                                  </span>
                                )}
                              </div>
                              {cInfo?.descricao && (
                                <p className="font-pixel text-[6.5px] leading-[1.5] text-muted-foreground break-words">
                                  {cInfo.descricao}
                                </p>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Combined grid dialog (all categories, no tabs) opened by clicking the card */}
      <Dialog open={combinedOpen} onOpenChange={setCombinedOpen}>
        <DialogContent className={`${isMobile ? "max-w-sm" : "max-w-2xl"} max-h-[85vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="font-pixel text-[11px] text-primary gold-text-glow truncate">
              {personagemNome}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const order = ["outfits", "montarias", "itens", "quests", "charms"];
            const groups = order
              .map((k) => selections.find((s) => s.key === k))
              .filter((s): s is (typeof selections)[number] => !!s && s.items.length > 0);
            if (groups.length === 0) {
              return (
                <p className="font-pixel text-[8px] text-muted-foreground py-4 text-center">
                  Nenhum item neste anúncio
                </p>
              );
            }
            return (
              <div className="flex flex-col gap-3 mt-1">
                {groups.map((sel) => {
                  const collapsed = !!collapsedCats[sel.key];
                  const gallery = !!galleryCats[sel.key];
                  return (
                  <div key={sel.key}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <button
                        type="button"
                        onClick={() => setCollapsedCats((p) => ({ ...p, [sel.key]: !p[sel.key] }))}
                        className="flex items-center gap-1.5 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                        aria-label={collapsed ? "Expandir categoria" : "Retrair categoria"}
                      >
                        {collapsed ? (
                          <ChevronRight className="w-3 h-3 text-primary shrink-0" />
                        ) : (
                          <ChevronDown className="w-3 h-3 text-primary shrink-0" />
                        )}
                        <sel.icon className="w-3 h-3 text-primary shrink-0" />
                        <span className="font-pixel text-[8px] text-primary truncate">
                          {(sel.tabLabel ?? sel.label)} ({sel.items.length})
                        </span>
                      </button>
                      <div className="flex items-center gap-1 tibia-border-inner rounded-sm p-0.5 bg-stone-dark/40 shrink-0">
                        <button
                          type="button"
                          onClick={() => setGalleryCats((p) => ({ ...p, [sel.key]: false }))}
                          className={`p-1 rounded-sm transition-colors ${!gallery ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
                          aria-label="Ver em grade"
                        >
                          <LayoutGrid className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setGalleryCats((p) => ({ ...p, [sel.key]: true }))}
                          className={`p-1 rounded-sm transition-colors ${gallery ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
                          aria-label="Ver em galeria"
                        >
                          <Images className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {!collapsed && (
                    <div className={`overflow-y-auto tibia-border-inner rounded-sm bg-stone-dark/20 p-2 ${gallery ? "max-h-[60vh]" : "max-h-[220px]"}`}>
                      {sel.key === "quests" ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {sel.items.map((it, i) => {
                            const img = getItemImg(sel.imageKind, it);
                            return (
                              <button
                                type="button"
                                key={`${it}-${i}`}
                                onClick={() => openDetail(sel.imageKind, it)}
                                className="tibia-border-inner rounded-sm bg-stone-dark/40 px-2 py-1.5 flex items-center gap-1.5 text-left transition-all hover:brightness-110 hover:ring-1 hover:ring-primary/60 cursor-pointer"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                                <span className="w-6 h-6 shrink-0 flex items-center justify-center bg-stone-dark/40 rounded-sm overflow-hidden">
                                  {img ? (
                                    <img
                                      src={img}
                                      alt={it}
                                      loading="lazy"
                                      className="w-full h-full object-contain"
                                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                                    />
                                  ) : (
                                    <span className="font-pixel text-[7px] text-muted-foreground/50">?</span>
                                  )}
                                </span>
                                <span className="font-pixel text-[6.5px] leading-tight text-foreground break-words line-clamp-2 min-w-0">
                                  {it}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                      <div className={gallery ? "grid grid-cols-2 sm:grid-cols-3 gap-3" : "grid grid-cols-3 sm:grid-cols-4 gap-2.5"}>
                        {sel.items.map((it, i) => {
                          const img = getItemImg(sel.imageKind, it);
                          const tierVal = sel.imageKind === "item" ? extractTier(it) : null;
                          return (
                            <button
                              type="button"
                              key={`${it}-${i}`}
                              onClick={() => openDetail(sel.imageKind, it)}
                              className={`tibia-border-inner rounded-sm bg-stone-dark/40 p-2 flex flex-col items-center justify-start text-center transition-all hover:brightness-110 hover:ring-1 hover:ring-primary/60 cursor-pointer ${gallery ? "gap-2" : "gap-1.5"}`}
                            >
                              <div className={`relative ${gallery ? "w-full" : ""}`}>
                                <span className={`relative shrink-0 flex items-center justify-center bg-stone-dark/40 rounded-sm overflow-hidden ${gallery ? "w-full aspect-square" : sel.imageKind === "outfit" ? "w-20 h-20" : "w-12 h-12"}`}>
                                  {img ? (
                                    <img
                                      src={img}
                                      alt={it}
                                      loading="lazy"
                                      className={`w-full h-full object-contain ${sel.imageKind === "outfit" && !gallery ? "scale-110 object-right-bottom origin-bottom-right" : ""}`}
                                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                                    />
                                  ) : (
                                    <span className="font-pixel text-[8px] text-muted-foreground/50">?</span>
                                  )}
                                  {tierVal && (
                                    <span className={`absolute top-0 right-0 rounded-full bg-[#FF8C00] flex items-center justify-center border-2 border-[#FF6B00] shadow-md ${gallery ? "w-5 h-5" : "w-4 h-4"}`}>
                                      <span className={`font-pixel font-bold text-white leading-none ${gallery ? "text-[7px]" : "text-[6px]"}`}>{tierVal}</span>
                                    </span>
                                  )}
                                </span>
                                {sel.imageKind === "outfit" && (() => {
                                  const label = outfitAddonBadge(decodeOutfit(it).addons);
                                  return label ? (
                                    <span className="absolute top-1 left-1 z-10 px-1.5 py-0.5 rounded-sm bg-primary text-primary-foreground text-[8px] font-bold leading-none whitespace-nowrap shadow">
                                      {label}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                              <span className={`font-pixel leading-tight text-foreground break-words line-clamp-2 ${gallery ? "text-[8px]" : "text-[6.5px]"}`}>
                                {sel.imageKind === "item" ? stripTier(it) : sel.imageKind === "outfit" ? decodeOutfit(it).nome : it}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      )}
                    </div>
                    )}
                  </div>
                  );
                })}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Item detail dialog (larger image + name + extra info) */}
      <Dialog open={!!itemDetail} onOpenChange={(o) => { if (!o) setItemDetail(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-pixel text-[11px] text-primary gold-text-glow break-words leading-relaxed flex items-center gap-1.5 justify-center flex-wrap">
              <span>{itemDetail?.name}</span>
              {itemDetail?.tier && (
                <span className="inline-flex w-5 h-5 rounded-full bg-[#FF8C00] items-center justify-center border-2 border-[#FF6B00] shadow-md">
                  <span className="font-pixel text-[7px] font-bold text-white leading-none">{itemDetail.tier}</span>
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {itemDetail && (
            <div className="flex flex-col items-center gap-3 mt-1">
              <span className="relative w-32 h-32 flex items-center justify-center bg-stone-dark/40 tibia-border-inner rounded-sm overflow-hidden">
                {itemDetail.img ? (
                  <img
                    src={itemDetail.img}
                    alt={itemDetail.name}
                    className={`w-full h-full object-contain ${itemDetail.kind === "outfit" ? "scale-110 object-right-bottom origin-bottom-right" : ""}`}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                  />
                ) : (
                  <span className="font-pixel text-[14px] text-muted-foreground/50">?</span>
                )}
              </span>

              {itemDetail.loading ? (
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              ) : itemDetail.info.length > 0 ? (
                <div className="w-full flex flex-col gap-1.5">
                  {itemDetail.info.map((row) => (
                    <div
                      key={row.label}
                      className="flex flex-col gap-0.5 tibia-border-inner rounded-sm bg-stone-dark/30 px-2 py-1.5"
                    >
                      <span className="font-pixel text-[7px] text-primary">{row.label}</span>
                      <span className="font-pixel text-[7.5px] leading-[1.5] text-foreground break-words">{row.value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="font-pixel text-[7px] text-muted-foreground py-1 text-center">
                  Sem informações adicionais
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>




      <ReportProblemDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        anuncioId={anuncioId}
        itemNome={personagemNome}
        tier={tier}
        servidor={servidor}
        onReported={() => setIsReported(true)}
      />

      <MediaCarouselDialog
        open={mediaOpen}
        onOpenChange={setMediaOpen}
        midias={midias}
        personagemNome={personagemNome}
      />

      {sessionContato && sessionToken && (
        <SolicitarIntermedioDialog
          open={intermedioOpen}
          onOpenChange={setIntermedioOpen}
          anuncioId={anuncioId}
          itemNome={personagemNome}
          preco={preco}
          moeda={moeda}
          precoCoins={precoCoins}
          precoKk={precoKk}
          servidor={servidor}
          descricao={descricao}
          anuncianteApelido={apelido}
          anuncianteAvatarUrl={anuncianteAvatarUrl}
          sessionContato={sessionContato}
          sessionToken={sessionToken}
        />
      )}
    </>
  );
};

export default memo(PersonagemCard);
